import { useEffect } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { TopNav } from "./components/TopNav";
import { ConnectionBanner } from "./components/ConnectionBanner";
import { FleetAnnouncer } from "./components/Announcer";
import { ToastViewport } from "./components/Toast";
import { OnboardingCompany } from "./components/OnboardingCompany";
import { PairDevice } from "./pages/PairDevice";
import { bindAgentDispatch, ws } from "./api/ws";
import { companiesApi } from "./api/queries/companies";
import { useUiStore } from "./stores/ui";
import { useShortcuts } from "./hooks/useShortcuts";

export function AppShell() {
  const nav = useNavigate();
  const setPair = useUiStore((s) => s.setPairModalOpen);
  const closeDrawer = useUiStore((s) => s.closeDrawer);

  // First-run onboarding: if the user belongs to zero companies, show the
  // create-company form instead of the empty dashboard. The rest of the SPA
  // assumes an active company, so this is the only path to make one.
  const companies = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });
  const noCompanies =
    companies.isSuccess && (companies.data?.companies.length ?? 0) === 0;

  // Single global WS — connect on mount, disconnect on unmount. The agent
  // dispatcher is bound to the same connection so `agent.*` envelopes flow
  // straight into the chat / permission stores without per-component wiring.
  useEffect(() => {
    const unbind = bindAgentDispatch();
    ws.connect();
    return () => {
      unbind();
      ws.disconnect();
    };
  }, []);

  useShortcuts({
    focusSearch: () => {
      const el = document.getElementById("global-search-input");
      if (el instanceof HTMLInputElement) {
        el.focus();
        el.select();
      }
    },
    openPair: () => setPair(true),
    goDevices: () => nav({ to: "/" }),
    goWorkOrders: () => nav({ to: "/work-orders" }),
    closeOverlay: () => {
      setPair(false);
      closeDrawer();
    },
  });

  if (noCompanies) {
    return (
      <div className="min-h-screen bg-canvas text-primary">
        <OnboardingCompany />
        <ToastViewport />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-primary">
      <TopNav />
      <ConnectionBanner />
      <FleetAnnouncer />
      <Outlet />
      <PairDevice />
      <ToastViewport />
    </div>
  );
}
